import React, { useState } from 'react';
import { motion } from 'framer-motion';
import CalendarModal from './CalendarModal';
import { useLanguage } from '../contexts/LanguageContext';

const WeddingDetails = () => {
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const { t } = useLanguage();

  const details = [
    {
      icon: 'fas fa-church',
      id: 'ceremony',
      title: t('weddingDetails.ceremony.title'),
      location: t('weddingDetails.ceremony.location'),
      address: t('weddingDetails.ceremony.address'),
      time: t('weddingDetails.ceremony.time'),
      mapLink: 'https://maps.app.goo.gl/g3dADnESxLcFBvL77'
    },
    {
      icon: 'fas fa-glass-cheers',
      id: 'party',
      title: t('weddingDetails.reception.title'),
      location: t('weddingDetails.reception.location'),
      address: t('weddingDetails.reception.address'),
      time: t('weddingDetails.reception.time'),
      mapLink: 'https://maps.app.goo.gl/Sk99eSy14jBpkzZy9'
    }
  ];

  const handleAddToCalendar = (eventType) => {
    setSelectedEvent(eventType);
    setShowCalendarModal(true);
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  // Icon animation variants
  const iconVariants = {
    initial: { 
      scale: 1,
      rotate: 0,
      y: 0
    },
    animate: {
      scale: [1, 1.1, 1],
      rotate: [0, 5, -5, 0],
      y: [0, -10, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    },
    hover: {
      scale: 1.2,
      rotate: 10,
      y: -15,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  // Ceremony specific animation (gentle floating)
  const ceremonyIconVariants = {
    initial: { 
      scale: 1,
      y: 0,
      rotate: 0
    },
    animate: {
      y: [0, -8, 0],
      rotate: [0, 2, -2, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    },
    hover: {
      scale: 1.15,
      y: -12,
      rotate: 5,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  // Reception specific animation (cheerful bouncing)
  const receptionIconVariants = {
    initial: { 
      scale: 1,
      y: 0,
      rotate: 0
    },
    animate: {
      scale: [1, 1.05, 1],
      y: [0, -12, 0],
      rotate: [0, -3, 3, 0],
      transition: {
        duration: 2.5,
        repeat: Infinity,
        ease: "easeInOut"
      }
    },
    hover: {
      scale: 1.2,
      y: -18,
      rotate: -8,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="details" className="pt-5 bg-white">
      <div className="container">
        <motion.div 
          className="text-center mb-5"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="section-title">{t('weddingDetails.title')}<div className="title-decoration"><img src="/images/section-title.png" alt="Decorative element" /></div></h2>
          <p className="lead">{t('weddingDetails.subtitle')}</p>
        </motion.div>

        <div className="row justify-content-around">
          {details.map((detail, index) => (
            <motion.div 
              key={detail.id}
              className="col-lg-5 col-md-8 col-sm-10 mx-auto mb-5"
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
            >
              <div className="card border-0 shadow h-100">
                <div className="card-body text-center">
                  <div className="icon-container mb-4">
                    <motion.i 
                      className={`${detail.icon} fa-3x`} 
                      style={{color: "var(--primary-color)"}}
                      variants={detail.id === 'ceremony' ? ceremonyIconVariants : receptionIconVariants}
                      initial="initial"
                      animate="animate"
                      whileHover="hover"
                    ></motion.i>
                  </div>
                  
                  <h4 className="card-title mb-3">{detail.title}</h4>
                  
                  <div className="detail-info mb-4">
                    <p className="mb-2">
                      <i className="fas fa-clock me-2" style={{color: "var(--primary-color)"}}></i>
                      <strong>{detail.time}</strong>
                    </p>
                    <p className="mb-2">
                      <i className="fas fa-map-marker-alt me-2" style={{color: "var(--primary-color)"}}></i>
                      <strong>{detail.location}</strong>
                    </p>
                    <p className="mb-0 text-muted">
                      <i className="fas fa-location-arrow me-2" style={{color: "var(--primary-color)"}}></i>
                      {detail.address}
                    </p>
                    {detail.id === 'party' && (
                      <p className="mb-0 text-muted mt-2">
                        <i className="fas fa-parking me-2" style={{color: "var(--primary-color)"}}></i>
                        {t('weddingDetails.reception.parking')}
                      </p>
                    )}
                  </div>
                  
                  <div className="calendar-actions mt-3 d-flex justify-content-center">
                  <button 
                      className="btn btn-outline-secondary btn-sm ms-2" 
                      onClick={() => handleAddToCalendar(detail.id)}
                    >
                      <i className="fas fa-plus me-2"></i>{t('weddingDetails.addToCalendar')}
                    </button>
                    <button 
                      className="btn btn-outline-secondary btn-sm ms-2" 
                      onClick={() => window.open(detail.mapLink, "_blank")}
                    >
                      <i className="fas fa-map-marker-alt me-2"></i>{t('weddingDetails.getDirections')}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <CalendarModal 
        show={showCalendarModal}
        onHide={() => setShowCalendarModal(false)}
        eventType={selectedEvent}
      />
    </section>
  );
};

export default WeddingDetails;
